import type { GHAEmailClassification } from '~/services/ConnectionGoogleService';

import {
  GHA_EMAIL_CLASSIFICATION_OPTIONS,
  GHA_EMAIL_DOMAIN_RULES_DEFAULT_PAGE_SIZE,
  GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS,
} from './constants';

// Same format the API enforces: lower-case labels joined by dots, at least two labels.
const EMAIL_DOMAIN_PATTERN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/** Mirrors the API's normalisation, so the dialog can validate before sending: " @Hyatt.com " → "hyatt.com". */
export function normaliseEmailDomain(value: string): string {
  const domain = value.trim().toLowerCase().replace(/^@/, '');
  return domain.endsWith('.') ? domain.slice(0, -1) : domain;
}

export function isValidEmailDomain(domain: string): boolean {
  return EMAIL_DOMAIN_PATTERN.test(domain);
}

/** Zero-based page and page size from the URL; anything unparseable or unsupported falls back to the defaults. */
export function parsePaginationParams(searchParams: URLSearchParams): { page: number; pageSize: number } {
  const page = Number(searchParams.get('page'));
  const pageSize = Number(searchParams.get('pageSize'));
  return {
    page: Number.isInteger(page) && page > 0 ? page : 0,
    pageSize: GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS.includes(pageSize)
      ? pageSize
      : GHA_EMAIL_DOMAIN_RULES_DEFAULT_PAGE_SIZE,
  };
}

/** Classification filter and domain search from the URL; an unknown classification means no filter. */
export function parseFilterParams(searchParams: URLSearchParams): {
  classification: '' | GHAEmailClassification;
  domain: string;
} {
  const classification = GHA_EMAIL_CLASSIFICATION_OPTIONS.find(
    (option) => option.value === searchParams.get('classification'),
  );
  return {
    classification: classification?.value ?? '',
    domain: searchParams.get('domain') ?? '',
  };
}

/**
 * The expired toggle for the blocked-identities tab, from the URL. The search text is never kept in the URL:
 * it is often a whole address, which must stay out of URLs.
 */
export function parseBlockedIdentityFilterParams(searchParams: URLSearchParams): {
  includeExpired: boolean;
} {
  return {
    includeExpired: searchParams.get('includeExpired') === 'true',
  };
}

// A loose shape check so the dialog can catch typos; the API canonicalises and has the final say.
const EMAIL_ADDRESS_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function isValidEmailAddress(value: string): boolean {
  return EMAIL_ADDRESS_PATTERN.test(value);
}

/** Expiry for a date input (`2027-10-01`). The API stores the expiry as that date's UTC midnight. */
export function toExpiryDate(expiresAt: string | null): string {
  return expiresAt == null ? '' : expiresAt.slice(0, 10);
}

export function fromExpiryDate(date: string): string | null {
  return date === '' ? null : `${date}T00:00:00.000Z`;
}
